import React, { useState, useEffect } from "react";
import { BiDownload, BiFilter, BiRefresh, BiCalendar } from "react-icons/bi";
import { useSubscriptionReport } from "../../../hooks";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatDisplayDate,
  translateStatus,
  getStatusColor,
} from "../../../utils/formatters";
import {
  ReportFilters,
  ReportTabs,
  KPICard,
  DataTable,
  ExportModal,
} from "./shared";
import { toast } from "react-toastify";
import { exportReport } from "../../../utils/reportExport";
import { buildSubscriptionsExport } from "./reportExports";

const SubscriptionsReport = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const [showFilters, setShowFilters] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(null);
  const [filters, setFilters] = useState({
    period: "month",
    startDate: null,
    endDate: null,
    status: null,
    expiringSoon: false,
  });

  const {
    loading,
    error,
    summary,
    getSummary,
    getList,
    getServiceUsage,
    calculateMRR,
    calculateRenewalRate,
    prepareTimelineData,
  } = useSubscriptionReport();

  const [subscriptionSummary, setSubscriptionSummary] = useState(null);
  const [subscriptions, setSubscriptions] = useState({
    data: [],
    pagination: {},
  });
  const [serviceUsage, setServiceUsage] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  useEffect(() => {
    loadTabData();
  }, [activeTab, filters]);

  const loadTabData = async () => {
    try {
      switch (activeTab) {
        case "summary":
          const summaryData = await getSummary(filters);
          setSubscriptionSummary(summaryData);
          break;
        case "list":
          const listResult = await getList({ ...filters, page: 1 });
          setSubscriptions(listResult);
          break;
        case "usage":
          const usageResult = await getServiceUsage(filters);
          setServiceUsage(usageResult || []);
          break;
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
    }
  };

  const handlePageChange = async (page) => {
    const result = await getList({ ...filters, page });
    setSubscriptions(result);
  };

  const handleRowClick = (subscription) => {
    setSelectedSubscription(subscription);
    setShowSubscriptionDetails(true);
  };

  const handleExport = async (format, options) => {
    try {
      const config = await buildSubscriptionsExport(filters, activeTab);
      await exportReport(config, format, options);
      toast.success("Export du rapport terminé");
    } catch (error) {
      console.error("Erreur export:", error);
      toast.error(error?.message || "Erreur lors de l'export du rapport");
      // Laisser la fenêtre d'export ouverte pour permettre un nouvel essai
      throw error;
    }
  };

  const tabs = [
    { id: "summary", label: "Résumé" },
    { id: "list", label: "Liste", count: subscriptions.pagination?.total },
    { id: "usage", label: "Utilisation services" },
  ];

  const subscriptionColumns = [
    {
      key: "code",
      label: "Code",
      render: (row) => <span className="font-mono">{row.code}</span>,
    },
    {
      key: "clientName",
      label: "Client",
      render: (row) => row.clientName || "Anonyme",
    },
    { key: "planName", label: "Formule" },
    {
      key: "amount",
      label: "Montant",
      render: (row) => formatCurrency(row.amount),
    },
    {
      key: "startDate",
      label: "Début",
      render: (row) => formatDisplayDate(row.startDate),
    },
    {
      key: "endDate",
      label: "Fin",
      render: (row) => formatDisplayDate(row.endDate),
    },
    {
      key: "daysLeft",
      label: "Jours restants",
      render: (row) => (
        <span
          className={row.daysLeft < 7 ? "text-orange-600 font-semibold" : ""}
        >
          {formatNumber(row.daysLeft)}
        </span>
      ),
    },
    {
      key: "status",
      label: "Statut",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${getStatusColor(row.status, "subscription")}`}
        >
          {translateStatus(row.status, "subscription")}
        </span>
      ),
    },
  ];

  const usageColumns = [
    { key: "serviceName", label: "Service" },
    {
      key: "totalAvailable",
      label: "Disponibles",
      render: (row) => formatNumber(row.totalAvailable),
    },
    {
      key: "totalUsed",
      label: "Utilisés",
      render: (row) => formatNumber(row.totalUsed),
    },
    {
      key: "usageRate",
      label: "Taux utilisation",
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${
                row.usageRate > 75
                  ? "bg-green-500"
                  : row.usageRate > 50
                    ? "bg-blue-500"
                    : row.usageRate > 25
                      ? "bg-yellow-500"
                      : "bg-red-500"
              }`}
              style={{ width: `${row.usageRate}%` }}
            />
          </div>
          <span>{formatPercentage(row.usageRate)}</span>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        Une erreur est survenue : {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-poppins font-semibold text-[20px] text-title">
          Rapport des abonnements
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiFilter size={18} />
            Filtres
          </button>
          <button
            onClick={() => setShowExport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors"
          >
            <BiDownload size={18} />
            Exporter
          </button>
          <button
            onClick={loadTabData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiRefresh size={18} />
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Onglets */}
      <ReportTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Contenu */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
        </div>
      ) : (
        <>
          {activeTab === "summary" && subscriptionSummary && (
            <div className="space-y-6">
              {/* KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                  title="Abonnements actifs"
                  value={formatNumber(
                    subscriptionSummary.overview.activeSubscriptions,
                  )}
                  icon="✅"
                  color="green"
                />
                <KPICard
                  title="MRR"
                  value={formatCurrency(calculateMRR(subscriptionSummary))}
                  icon="💰"
                  color="blue"
                />
                <KPICard
                  title="Taux renouvellement"
                  value={formatPercentage(
                    calculateRenewalRate(subscriptionSummary),
                  )}
                  icon="🔄"
                  color="purple"
                />
                <KPICard
                  title="Valeur moyenne"
                  value={formatCurrency(
                    subscriptionSummary.overview.averageSubscriptionValue,
                  )}
                  icon="📊"
                  color="orange"
                />
              </div>

              {/* Répartition par statut */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard
                  title="Expirés"
                  value={formatNumber(
                    subscriptionSummary.overview.expiredSubscriptions,
                  )}
                  icon="⌛"
                  color="gray"
                />
                <KPICard
                  title="Annulés"
                  value={formatNumber(
                    subscriptionSummary.overview.cancelledSubscriptions,
                  )}
                  icon="❌"
                  color="red"
                />
                <KPICard
                  title="CA total"
                  value={formatCurrency(
                    subscriptionSummary.overview.totalRevenue,
                  )}
                  icon="💰"
                  color="green"
                />
              </div>

              {/* Abonnements par formule */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-poppins font-semibold text-[16px] mb-4">
                  Répartition par formule
                </h3>
                <div className="space-y-4">
                  {subscriptionSummary.byPlan.map((plan) => (
                    <div key={plan.planId} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-nunito font-medium text-[14px]">
                            {plan.planName}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">
                            {formatCurrency(plan.planPrice)}
                          </span>
                        </div>
                        <span className="font-nunito text-[14px] font-semibold">
                          {formatNumber(plan.activeCount)}/
                          {formatNumber(plan.count)} actifs
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-secondary-500 rounded-full"
                          style={{ width: `${plan.percentage}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>CA: {formatCurrency(plan.revenue)}</span>
                        <span>{formatPercentage(plan.percentage)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Évolution mensuelle */}
              {subscriptionSummary.byMonth && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-poppins font-semibold text-[16px] mb-4">
                    Évolution mensuelle
                  </h3>
                  <div className="space-y-3">
                    {prepareTimelineData(subscriptionSummary).map((month) => (
                      <div
                        key={month.period}
                        className="flex items-center gap-4"
                      >
                        <span className="w-24 font-nunito text-[14px]">
                          {month.period}
                        </span>
                        <div className="flex-1 flex items-center gap-4">
                          <div className="flex-1">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-green-500 rounded-full"
                                style={{
                                  width: `${(month["Nouveaux abonnements"] / Math.max(...prepareTimelineData(subscriptionSummary).map((m) => m["Nouveaux abonnements"]))) * 100}%`,
                                }}
                              />
                            </div>
                          </div>
                          <span className="w-16 text-right">
                            {formatNumber(month["Nouveaux abonnements"])}
                          </span>
                          <span className="w-24 text-right text-green-600">
                            {formatCurrency(month["Revenus"])}
                          </span>
                          <span className="w-16 text-right text-red-500">
                            -{formatNumber(month["Annulations"])}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Abonnements expirant bientôt */}
              {subscriptionSummary.expiringSoon &&
                subscriptionSummary.expiringSoon.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                    <h3 className="font-poppins font-semibold text-[16px] mb-4 flex items-center gap-2">
                      <BiCalendar className="text-orange-600" />
                      Abonnements expirant bientôt
                    </h3>
                    <div className="space-y-3">
                      {subscriptionSummary.expiringSoon.map((item) => (
                        <div
                          key={item.subscriptionId}
                          className="flex items-center justify-between bg-white p-3 rounded-lg"
                        >
                          <div>
                            <p className="font-medium">{item.clientName}</p>
                            <p className="text-sm text-gray-600">
                              {item.planName}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">
                              Expire le {formatDisplayDate(item.endDate)}
                            </p>
                            <p className="text-orange-600 font-semibold">
                              {item.daysLeft} jours
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Période */}
              <div className="text-sm text-gray-500 text-right">
                Période du {formatDisplayDate(subscriptionSummary.period.start)}{" "}
                au {formatDisplayDate(subscriptionSummary.period.end)}
              </div>
            </div>
          )}

          {activeTab === "list" && (
            <DataTable
              columns={subscriptionColumns}
              data={subscriptions.data}
              pagination={subscriptions.pagination}
              onPageChange={handlePageChange}
              onRowClick={handleRowClick}
            />
          )}

          {activeTab === "usage" && (
            <DataTable
              columns={usageColumns}
              data={serviceUsage}
              emptyMessage="Aucune donnée d'utilisation disponible"
            />
          )}
        </>
      )}

      {/* Modal détails abonnement */}
      {showSubscriptionDetails && selectedSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-poppins font-semibold text-[18px]">
                Détails abonnement {selectedSubscription.code}
              </h3>
              <button
                onClick={() => setShowSubscriptionDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Infos client */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Client</h4>
                <p className="text-lg">{selectedSubscription.clientName}</p>
                <p className="text-sm text-gray-600">
                  {selectedSubscription.clientEmail}
                </p>
                <p className="text-sm text-gray-600">
                  {selectedSubscription.clientPhone}
                </p>
              </div>

              {/* Infos abonnement */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Formule</p>
                  <p className="font-medium">{selectedSubscription.planName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Prix formule</p>
                  <p className="font-medium">
                    {formatCurrency(selectedSubscription.planPrice)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Montant payé</p>
                  <p className="font-medium text-green-600">
                    {formatCurrency(selectedSubscription.amount)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Statut</p>
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedSubscription.status, "subscription")}`}
                  >
                    {translateStatus(
                      selectedSubscription.status,
                      "subscription",
                    )}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date début</p>
                  <p className="font-medium">
                    {formatDisplayDate(selectedSubscription.startDate)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date fin</p>
                  <p className="font-medium">
                    {formatDisplayDate(selectedSubscription.endDate)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Jours restants</p>
                  <p
                    className={`font-medium ${selectedSubscription.daysLeft < 7 ? "text-orange-600" : ""}`}
                  >
                    {formatNumber(selectedSubscription.daysLeft)} jours
                  </p>
                </div>
              </div>

              {/* Services inclus */}
              {selectedSubscription.services &&
                selectedSubscription.services.length > 0 && (
                  <div>
                    <h4 className="font-poppins font-semibold text-[16px] mb-3">
                      Services inclus
                    </h4>
                    <div className="space-y-2">
                      {selectedSubscription.services.map((service, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center p-3 bg-gray-50 rounded"
                        >
                          <span>{service.serviceName}</span>
                          {service.used ? (
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-600">
                                Utilisé le {formatDisplayDate(service.usedAt)}
                              </span>
                              {service.usedFor && (
                                <span className="text-xs font-mono bg-gray-200 px-2 py-1 rounded">
                                  {service.usedFor}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-green-600">
                              Non utilisé
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Modal d'export */}
      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          onExport={handleExport}
        />
      )}
    </div>
  );
};

export default SubscriptionsReport;
